<template>
	<ChalkyUtilityModal
		class="view-modal-global-addvideo default-modal"
		v-bind:class="stateClass"
		v-model="isVisible"
		v-on:hide="Handle_OnHide"
	>
		<ChalkyUtilitySlideshow
			ref="slideshowView"
			v-bind:showDots="false"
			v-bind:showNavigation="false"
			v-bind:slideCount="4"
			v-on:change="Handle_OnSlideChange"
		>
			<!-- Slide 0: Select Video -->
			<template #slide-0>
				<header class="stepped-overlay-header">
					<h3>Add a Video</h3>
					<p>Show off your skills</p>
				</header>

				<section class="primary-container stepped-overlay-content">
					<ChalkyFormMediaUploader
						ref="formMediaUploader"
						type="video"
						v-bind:allowMultiple="false"
						v-bind:model="userModel"
						v-bind:showDefaultActions="false"
						v-on:complete="Handle_OnVideoUploaded"
						v-on:files="Handle_OnVideoFiles"
						v-on:upload-error="Handle_OnVideoUploadError"
						v-on:upload-start="Handle_OnVideoUploadStart"
						variant="dropzone"
					/>
				</section>

				<section class="action-container stepped-overlay-actions">
					<div class="button-group">
						<button
							class="button-primary"
							v-bind:disabled="fileCount === 0"
							v-on:click="Handle_OnClickContinue"
						>
							Continue
						</button>
					</div>
				</section>
			</template>

			<!-- Slide 1: Add Details -->
			<template #slide-1>
				<header class="stepped-overlay-header">
					<h3>Add Details</h3>
					<p>Give your video some context</p>
				</header>

				<section class="primary-container stepped-overlay-content form-container">
					<fieldset>
						<label>
							<h6>Title</h6>
							<input
								type="text"
								placeholder="e.g., 9-ball run out"
								v-model="videoTitle"
							/>
						</label>

						<label>
							<h6>Description (optional)</h6>
							<textarea
								placeholder="Tell us about this shot or run..."
								rows="3"
								v-model="videoDescription"
							></textarea>
						</label>
					</fieldset>
				</section>

				<section class="action-container stepped-overlay-actions">
					<div class="button-group">
						<button class="button-secondary" v-on:click="Handle_OnClickBack">Back</button>
						<button class="button-primary" v-on:click="Handle_OnClickContinue">Continue</button>
					</div>
				</section>
			</template>

			<!-- Slide 2: Tag Content -->
			<template #slide-2>
				<header class="stepped-overlay-header">
					<h3>Tag Your Video</h3>
					<p>Help others find your content</p>
				</header>

				<section class="primary-container stepped-overlay-content">
					<!-- Venue tagging -->
					<section class="venue-container">
						<h5>Where was this filmed?</h5>

						<ChalkyVenueRow v-if="selectedVenueModel" v-bind:venueModel="selectedVenueModel">
							<template v-slot:actions>
								<button
									class="control round size-medium theme-dark type-close"
									v-on:click="Handle_OnClickClearVenue"
								>
									Clear
								</button>
							</template>
						</ChalkyVenueRow>

						<ChalkyFormSearch
							class="size-small"
							placeholder="Search for a place"
							v-bind:autocomplete="true"
							v-bind:autocompleteDelay="300"
							v-bind:type="'venue'"
							v-bind:venueCollection="venueCollection"
							v-else
							v-on:autocomplete:select="Handle_OnSelectVenue"
						/>
					</section>

					<!-- User tagging -->
					<section class="user-container">
						<h5>Who's in the video?</h5>

						<ChalkyUserRow v-if="selectedUserModel" v-bind:userModel="selectedUserModel">
							<template v-slot:actions>
								<button
									class="control round size-medium theme-dark type-close"
									v-on:click="Handle_OnClickClearUser"
								>
									Clear
								</button>
							</template>
						</ChalkyUserRow>

						<ChalkyFormSearch
							class="size-small"
							placeholder="Search for a person"
							v-bind:autocomplete="true"
							v-bind:autocompleteDelay="300"
							v-bind:type="'user'"
							v-bind:userCollection="userCollection"
							v-else
							v-on:autocomplete:select="Handle_OnSelectUser"
						/>
					</section>
				</section>

				<section class="action-container stepped-overlay-actions">
					<div class="button-group">
						<button class="button-secondary" v-on:click="Handle_OnClickBack">Back</button>
						<button class="button-primary" v-bind:disabled="isLoading" v-on:click="Handle_OnClickSubmit">
							<span v-if="isLoading">Uploading...</span>
							<span v-else>Upload</span>
						</button>
					</div>
				</section>
			</template>

			<!-- Slide 3: Confirmation -->
			<template #slide-3>
				<section class="primary-container stepped-overlay-content">
					<section class="complete-container stepped-overlay-complete">
						<figure class="push-bottom">
							<img
								class="icon filter-invert"
								src="~@chalkysticks/sass/build/asset/image/icon/checkmark.svg"
							/>
						</figure>

						<h3>Success!</h3>

						<p>
							Your video has been uploaded and is being processed. It may take a few minutes to appear.
						</p>
					</section>
				</section>

				<section class="action-container stepped-overlay-actions">
					<div class="button-group">
						<button class="button-secondary size-medium" v-on:click="Handle_OnClickClose">Close</button>
					</div>
				</section>
			</template>
		</ChalkyUtilitySlideshow>
	</ChalkyUtilityModal>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Ref } from 'vue-property-decorator';
	import { beforeDestroy, mounted } from '@/Utility/Decorators';

	/**
	 * Global modal for uploading videos.
	 * Four steps: Select Video → Add Details → Tag Content → Confirmation
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Modal/Global
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewModalGlobalAddVideo extends ViewBase {
		/**
		 * CSS state class based on the current step of the video upload flow.
		 *
		 * @return string
		 */
		public get stateClass(): string {
			switch (this.currentStep) {
				case 0:
					return 'state-selection';

				case 1:
					return 'state-details';

				case 2:
					return 'state-tagging';

				case 3:
					return 'state-complete';

				default:
					return 'state-selection';
			}
		}

		/**
		 * @type ChalkySticks/Model/User
		 */
		protected get userModel(): ChalkySticks.Model.User {
			return this.$store.getters['authentication/user'];
		}

		/**
		 * Reference to the form media uploader component
		 *
		 * @type any
		 */
		@Ref('formMediaUploader')
		readonly formMediaUploader!: any;

		/**
		 * Reference to the slideshow view for navigation
		 *
		 * @type any
		 */
		@Ref('slideshowView')
		protected slideshowView!: any;

		/**
		 * Current step in the modal flow
		 *
		 * @type number
		 */
		protected currentStep: number = 0;

		/**
		 * Number of files selected
		 *
		 * @type number
		 */
		protected fileCount: number = 0;

		/**
		 * Whether a request is in progress
		 *
		 * @type boolean
		 */
		protected isLoading: boolean = false;

		/**
		 * Whether the modal is visible
		 *
		 * @type boolean
		 */
		protected isVisible: boolean = false;

		/**
		 * The selected user model for tagging
		 *
		 * @type ChalkySticks.Model.User | null
		 */
		protected selectedUserModel: ChalkySticks.Model.User | null = null;

		/**
		 * The selected venue model for tagging
		 *
		 * @type ChalkySticks.Model.Venue | null
		 */
		protected selectedVenueModel: ChalkySticks.Model.Venue | null = null;

		/**
		 * Successfully uploaded media models
		 *
		 * @type ChalkySticks.Model.Media[]
		 */
		protected uploadedMedia: ChalkySticks.Model.Media[] = [];

		/**
		 * Collection for user search autocomplete
		 *
		 * @type ChalkySticks.Collection.User
		 */
		protected userCollection: ChalkySticks.Collection.User = ChalkySticks.Factory.User.collection({
			limit: 12,
		});

		/**
		 * Collection for venue search autocomplete
		 *
		 * @type ChalkySticks.Collection.Venue
		 */
		protected venueCollection: ChalkySticks.Collection.Venue = ChalkySticks.Factory.Venue.collection({
			limit: 8,
		});

		/**
		 * Video description
		 *
		 * @type string
		 */
		protected videoDescription: string = '';

		/**
		 * Video title
		 *
		 * @type string
		 */
		protected videoTitle: string = '';

		// region: Lifecycle
		// ---------------------------------------------------------------------------

		/**
		 * Attach event listeners on mount.
		 *
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			ChalkySticks.Core.Event.Bus.on('modal:video:open', this.Handle_OnOpen);
		}

		/**
		 * Detach event listeners before destroy.
		 *
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			ChalkySticks.Core.Event.Bus.off('modal:video:open', this.Handle_OnOpen);
		}

		// endregion: Lifecycle

		// region: Actions
		// ---------------------------------------------------------------------------

		/**
		 * Close the modal and reset state.
		 *
		 * @return void
		 */
		protected close(): void {
			this.isVisible = false;
			this.reset();
		}

		/**
		 * Open the modal.
		 *
		 * @return void
		 */
		protected open(): void {
			this.reset();
			this.isVisible = true;
		}

		/**
		 * Reset modal state to initial values.
		 *
		 * @return void
		 */
		protected reset(): void {
			this.currentStep = 0;
			this.fileCount = 0;
			this.isLoading = false;
			this.selectedUserModel = null;
			this.selectedVenueModel = null;
			this.uploadedMedia = [];
			this.videoDescription = '';
			this.videoTitle = '';

			if (this.slideshowView) {
				this.slideshowView.goto(0);
			}
		}

		// endregion: Actions

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @return Promise<void>
		 */
		protected async Handle_OnOpen(): Promise<void> {
			this.open();
		}

		/**
		 * @return Promise<void>
		 */
		protected async Handle_OnHide(): Promise<void> {
			this.close();
		}

		/**
		 * Handle close button click.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickClose(e?: PointerEvent): Promise<void> {
			e?.preventDefault();

			this.close();
		}

		/**
		 * Handle back button click — go to previous slide.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickBack(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.slideshowView.previous();
		}

		/**
		 * Handle continue button click — advance to next slide.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickContinue(e: PointerEvent): Promise<void> {
			e.preventDefault();

			if (this.currentStep === 0 && this.fileCount === 0) {
				return;
			}

			this.slideshowView.next();
		}

		/**
		 * Handle slideshow slide change.
		 *
		 * @param number index
		 * @return Promise<void>
		 */
		protected async Handle_OnSlideChange(index: number): Promise<void> {
			this.currentStep = index;
		}

		/**
		 * Handle file selection from uploader.
		 *
		 * @param number count
		 * @return Promise<void>
		 */
		protected async Handle_OnVideoFiles(count: number): Promise<void> {
			this.fileCount = count;
		}

		/**
		 * Handle video upload start.
		 *
		 * @return Promise<void>
		 */
		protected async Handle_OnVideoUploadStart(): Promise<void> {
			this.isLoading = true;
		}

		/**
		 * Handle video upload complete.
		 *
		 * @param ChalkySticks.Model.Media[] mediaModels
		 * @return Promise<void>
		 */
		protected async Handle_OnVideoUploaded(mediaModels: ChalkySticks.Model.Media[]): Promise<void> {
			this.uploadedMedia = mediaModels;
			this.isLoading = false;
			this.slideshowView.goto(3);

			// Dispatch success event
			ChalkySticks.Core.Event.Bus.dispatch('video:upload:complete', {
				mediaModels: this.uploadedMedia,
			});
		}

		/**
		 * Handle video upload error.
		 *
		 * @param any error
		 * @return Promise<void>
		 */
		protected async Handle_OnVideoUploadError(error: any): Promise<void> {
			this.isLoading = false;
			ChalkySticks.Utility.Log.error('video', 'Upload error:', error);

			ChalkySticks.Core.Event.Bus.dispatch('dialog:alert', {
				message: 'Failed to upload video. Please try again.',
				title: 'Error',
			});
		}

		/**
		 * Handle venue selection from search.
		 *
		 * @param ChalkySticks.Model.Venue venueModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectVenue(venueModel: ChalkySticks.Model.Venue): Promise<void> {
			this.selectedVenueModel = venueModel;
		}

		/**
		 * Handle clear venue button click.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickClearVenue(e: PointerEvent): Promise<void> {
			e.preventDefault();

			this.selectedVenueModel = null;
		}

		/**
		 * Handle user selection from search.
		 *
		 * @param ChalkySticks.Model.User userModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectUser(userModel: ChalkySticks.Model.User): Promise<void> {
			this.selectedUserModel = userModel;
		}

		/**
		 * Handle clear user button click.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickClearUser(e: PointerEvent): Promise<void> {
			e.preventDefault();
			this.selectedUserModel = null;
		}

		/**
		 * Handle submit button click — upload and tag.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickSubmit(e: PointerEvent): Promise<void> {
			e.preventDefault();

			if (this.isLoading) {
				return;
			}

			this.isLoading = true;

			try {
				// Upload media
				const mediaModels: ChalkySticks.Model.Media[] = await this.formMediaUploader.upload();
				const tagPromises: Promise<ChalkySticks.Model.MediaTag>[] = [];

				// Tag user and venue in parallel, and update metadata
				for (const mediaModel of mediaModels) {
					// Set title and description if provided
					if (this.videoTitle) {
						mediaModel.set({
							title: this.videoTitle,
						});
					}

					if (this.videoDescription) {
						mediaModel.set({
							description: this.videoDescription,
						});
					}

					// Save metadata if we have title or description
					if (this.videoTitle || this.videoDescription) {
						await mediaModel.save();
					}

					if (this.selectedUserModel) {
						tagPromises.push(mediaModel.tag(this.selectedUserModel));
					}

					if (this.selectedVenueModel) {
						tagPromises.push(mediaModel.tag(this.selectedVenueModel));
					}
				}

				await Promise.all(tagPromises);

				this.uploadedMedia = mediaModels;
				this.slideshowView.goto(3);

				// Dispatch success event
				ChalkySticks.Core.Event.Bus.dispatch('video:upload:complete', {
					mediaModels: this.uploadedMedia,
				});
			} catch (error) {
				ChalkySticks.Utility.Log.error('video', 'Failed to upload:', error);

				ChalkySticks.Core.Event.Bus.dispatch('dialog:alert', {
					message: 'Failed to upload video. Please try again.',
					title: 'Error',
				});
			} finally {
				this.isLoading = false;
			}
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-modal-global-addvideo {
		.upload-container {
			min-height: 150px;
		}

		.venue-container,
		.user-container {
			h5 {
				margin-bottom: 0.75rem;
			}
		}
	}
</style>
